import { BreadcrumbItem } from '../hub/content-types';
import { CLAIMS_APPROVED, COMPANY, PLANS, SITE_NAME, SITE_URL, SOCIAL_PROFILES, STORE_LINKS, canonicalUrl } from '../site.config';

export type JsonLdNode = Readonly<Record<string, unknown>>;

export const ORG_ID = `${SITE_URL}/#organization`;

/// sameAs: mağaza bağlantıları + site.config.ts SOCIAL_PROFILES (sosyal hesap = tek satır ekleme).
export function organizationLd(socialProfiles: readonly string[] = SOCIAL_PROFILES): JsonLdNode {
  return {
    '@type': 'Organization',
    '@id': ORG_ID,
    name: SITE_NAME,
    legalName: COMPANY.legalName,
    url: `${SITE_URL}/`,
    logo: `${SITE_URL}/assets/icons/icon-384x384.png`,
    email: COMPANY.email,
    telephone: COMPANY.phoneE164,
    taxID: COMPANY.taxNumber,
    address: {
      '@type': 'PostalAddress',
      streetAddress: COMPANY.streetAddress,
      addressLocality: COMPANY.district,
      addressRegion: COMPANY.city,
      addressCountry: 'TR',
    },
    sameAs: [STORE_LINKS.appStore, STORE_LINKS.googlePlay, ...socialProfiles],
  };
}

/// Gerçek puan/yorum verisi olmadığı için aggregateRating/review EKLENMEZ
/// (Google zengin sonuç göstermeyebilir; uydurma veri konmaz — spec kararı).
export function softwareApplicationLd(): JsonLdNode {
  return {
    '@type': 'SoftwareApplication',
    '@id': `${SITE_URL}/#app`,
    name: SITE_NAME,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'iOS, Android',
    inLanguage: 'tr',
    url: `${SITE_URL}/`,
    publisher: { '@id': ORG_ID },
    offers: PLANS.map((plan) => ({
      '@type': 'Offer',
      name: `${plan.name} plan`,
      price: plan.priceValue.toFixed(2),
      priceCurrency: 'TRY',
      url: `${SITE_URL}/fiyatlar`,
      availability: 'https://schema.org/InStock',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: plan.priceValue.toFixed(2),
        priceCurrency: 'TRY',
        valueAddedTaxIncluded: true,
        billingDuration: plan.isoDuration,
      },
    })),
  };
}

export interface FaqLdItem {
  readonly question: string;
  readonly answer: string;
  /// Onay bekleyen iddia: CLAIMS_APPROVED false iken görünmez, JSON-LD'ye de girmez.
  readonly pendingApproval?: boolean;
}

/// JSON-LD yalnız sayfada görünen maddeleri içermelidir (SSS sayfası da aynı süzgeci kullanır).
export function visibleFaqItems<T extends FaqLdItem>(items: readonly T[], claimsApproved: boolean = CLAIMS_APPROVED): T[] {
  return items.filter((item) => claimsApproved || !item.pendingApproval);
}

export function faqPageLd(items: readonly { question: string; answer: string }[]): JsonLdNode {
  return {
    '@type': 'FAQPage',
    mainEntity: items.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };
}

export const DEFAULT_IMAGE_URL = `${SITE_URL}/assets/site/og-image.png`;

/// 'assets/…' site yolu → mutlak adres.
export function absoluteAssetUrl(path: string): string {
  return `${SITE_URL}/${path}`;
}

export function breadcrumbTrailLd(trail: readonly BreadcrumbItem[]): JsonLdNode {
  return {
    '@type': 'BreadcrumbList',
    itemListElement: [{ name: 'Ana sayfa', path: '/' }, ...trail].map((item, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: item.name,
      item: canonicalUrl(item.path),
    })),
  };
}

export function breadcrumbLd(name: string, path: string): JsonLdNode {
  return breadcrumbTrailLd([{ name, path }]);
}

export function webPageLd(p: {
  readonly path: string;
  readonly title: string;
  readonly description: string;
  readonly dateModified: string;
  readonly type?: 'WebPage' | 'CollectionPage';
}): JsonLdNode {
  const url = canonicalUrl(p.path);
  return {
    '@type': p.type ?? 'WebPage',
    '@id': `${url}#webpage`,
    url,
    name: p.title,
    description: p.description,
    inLanguage: 'tr',
    dateModified: p.dateModified,
    publisher: { '@id': ORG_ID },
  };
}

export const personId = (path: string): string => `${canonicalUrl(path)}#person`;

export interface PersonInput {
  readonly path: string;
  readonly name: string;
  readonly jobTitle: string;
  readonly photo: string | null;
  readonly sameAs: readonly string[];
  readonly worksFor: string | null;
  readonly bio?: string;
}

export function personLd(a: PersonInput): JsonLdNode {
  return {
    '@type': 'Person',
    '@id': personId(a.path),
    name: a.name,
    jobTitle: a.jobTitle,
    url: canonicalUrl(a.path),
    ...(a.bio ? { description: a.bio } : {}),
    ...(a.photo ? { image: absoluteAssetUrl(a.photo) } : {}),
    ...(a.sameAs.length ? { sameAs: [...a.sameAs] } : {}),
    ...(a.worksFor ? { worksFor: { '@type': 'Organization', name: a.worksFor } } : {}),
  };
}

export interface BlogPostingInput {
  readonly path: string;
  readonly h1: string;
  readonly description: string;
  readonly publishedAt: string;
  readonly updatedAt: string;
  readonly ogImage: string | null;
  readonly tags: readonly string[];
  readonly author: { readonly path: string };
}

export function blogPostingLd(g: BlogPostingInput): JsonLdNode {
  const url = canonicalUrl(g.path);
  return {
    '@type': 'BlogPosting',
    '@id': `${url}#article`,
    headline: g.h1,
    description: g.description,
    datePublished: g.publishedAt,
    dateModified: g.updatedAt,
    inLanguage: 'tr',
    url,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    image: g.ogImage ? absoluteAssetUrl(g.ogImage) : DEFAULT_IMAGE_URL,
    author: { '@id': personId(g.author.path) },
    publisher: { '@id': ORG_ID },
    ...(g.tags.length ? { keywords: g.tags.join(', ') } : {}),
  };
}
